// src/pages/Marketplace/markets/DesignateSpotlightModal.tsx
import React, { useEffect, useState } from 'react';
import { ErrorBanner, Spinner } from '@/pages/Admin/shared';
import type { Env } from '@/pages/Admin/shared';
import {
  formatWeekKey,
  useSpotlightEligibleBusinesses,
  useSpotlightMutation,
} from '@/pages/Marketplace/shared';
import type { Market, SpotlightEligibleBusiness } from '@/pages/Marketplace/shared';

interface Props {
  market: Market;
  env: Env;
  /** Week keys the admin may still write — already filtered by the caller. */
  writableWeeks: string[];
  /** Row the admin clicked; must be one of `writableWeeks`. */
  initialWeek: string;
  /** Venue currently designated for `initialWeek`, when this is a replace. */
  currentBusinessName: string | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Designate or replace one market-week's Spotlight Bar.
 *
 * The week list is confined to the weeks the server reported as writable and the
 * venue list comes from the eligibility endpoint, so the happy path cannot
 * produce a rejected write. The gates are still re-run server-side on submit —
 * a venue can lose its enrollment between opening this modal and saving — and
 * whatever the server says comes back verbatim in the banner.
 */
export default function DesignateSpotlightModal({
  market, env, writableWeeks, initialWeek, currentBusinessName, onClose, onSaved,
}: Props): React.ReactElement {
  const [week, setWeek] = useState(initialWeek);
  const [businessId, setBusinessId] = useState('');
  const [businesses, setBusinesses] = useState<SpotlightEligibleBusiness[] | null>(null);

  const { fetchEligible, loading: loadingBusinesses, error: businessesError } =
    useSpotlightEligibleBusinesses(env);
  const { designate, loading: saving, error: saveError } = useSpotlightMutation(env);

  useEffect(() => {
    let cancelled = false;
    fetchEligible(market.id).then((items) => {
      if (!cancelled) setBusinesses(items);
    });
    return () => { cancelled = true; };
  }, [fetchEligible, market.id]);

  const handleSave = async () => {
    const result = await designate({
      market_id: market.id, week_start: week, business_id: businessId,
    });
    if (result) { onSaved(); onClose(); }
  };

  const noneEligible = businesses !== null && businesses.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-white font-semibold">
            {currentBusinessName ? 'Replace' : 'Designate'} Spotlight Bar — {market.name}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>

        <div className="p-6 space-y-4">
          {currentBusinessName && (
            <p className="text-xs text-gray-400">
              Currently <span className="text-gray-200">{currentBusinessName}</span> for{' '}
              {formatWeekKey(initialWeek)}. Saving replaces it.
            </p>
          )}

          <label className="block">
            <span className="text-xs text-gray-400">Week (Mondays only, future weeks only)</span>
            <select
              value={week}
              onChange={(e) => setWeek(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-2 text-sm mt-1"
            >
              {writableWeeks.map((w) => (
                <option key={w} value={w}>{formatWeekKey(w)}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs text-gray-400">
              Spotlight venue (gamification-enrolled + approved in this market)
            </span>
            {loadingBusinesses ? (
              <div className="mt-2"><Spinner size="sm" /></div>
            ) : (
              <select
                value={businessId}
                onChange={(e) => setBusinessId(e.target.value)}
                disabled={noneEligible}
                className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-2 text-sm mt-1 disabled:opacity-40"
              >
                <option value="">— select a venue —</option>
                {businesses?.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}
          </label>

          {noneEligible && (
            <p className="text-xs text-amber-300 bg-amber-950/40 border border-amber-800/50 rounded px-3 py-2">
              No venue in {market.name} is eligible yet. A venue qualifies once it is approved and
              has an active gamification enrollment (live geofence + at least one active reward).
            </p>
          )}

          {businessesError && <ErrorBanner message={businessesError} />}
          {saveError && <ErrorBanner message={saveError} />}
        </div>

        <div className="px-6 py-4 border-t border-gray-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-gray-300 hover:text-white">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !businessId || !week}
            className="px-4 py-2 text-sm font-medium bg-violet-600 hover:bg-violet-700 disabled:opacity-40 rounded"
          >
            {saving ? 'Saving…' : currentBusinessName ? 'Replace' : 'Designate'}
          </button>
        </div>
      </div>
    </div>
  );
}
